/**
 * Prefetch de datos y chunks al pasar el mouse por ítems principales del Sidebar,
 * y en idle para listas de productos (categorías, variantes, etc.).
 * Dedupe por sección + tenant para no saturar la red.
 *
 * ⭐ QUÉ FALTABA ACÁ, Y POR QUÉ SE NOTABA
 * El founder reportó que la PRIMERA entrada a una pantalla muestra el spinner varios segundos, y la
 * segunda no. Este archivo ya precargaba dos de las tres cosas que hacen falta —el chunk de la ruta
 * y los números de la barra de KPIs— pero **no la lista que la pantalla va a mostrar**, que es
 * justo la consulta lenta. Se precalentaba todo menos lo que el usuario estaba esperando.
 *
 * El arreglo NO es duplicar acá la consulta de cada pantalla: es llamar **al mismo `loader` de la
 * ruta**. El loader ya es la definición de «qué necesita esta pantalla al abrirse», y reescribirla
 * acá crearía dos definiciones que se desincronizan en silencio — el usuario pagaría DOS consultas
 * (la precalentada que nadie lee y la real) y el síntoma sería idéntico al de no precargar nada.
 *
 * 🪤 Esto sólo funciona porque los loaders **no bloquean**: disparan y devuelven `null`. Si alguno
 * volviera a devolver su promesa, además de congelar la navegación haría que pasar el mouse por el
 * sidebar dejara promesas colgadas. Lo vigila `src/routes/__tests__/loaders.noBloquean.test.tsx`.
 */

import { prefetchSection } from '@/lib/prefetchRouteChunks';
import { cargarProductos } from '@/routes/loaders/productos';
import { precargarCola, COLAS_DE_OPERACIONES } from '@/routes/loaders/opsVentas';
import {
  prefetchAbandonedCartsStats,
  prefetchCategoriesStats,
  prefetchEcommerceDeliveryMethodsStats,
  prefetchEcommerceDeliveryRulesStats,
  prefetchEcommerceOrdersStats,
  prefetchEcommercePagesStats,
  prefetchEcommercePaymentMethodsStats,
  prefetchExistenciasStats,
  prefetchLogisticsCouriersCoverageStats,
  prefetchModifiersStats,
  prefetchProductsStats,
  prefetchSalesOrdersStats,
  prefetchShalomAgenciesStats,
  prefetchTagsStats,
  prefetchTenantDomainsStats,
  prefetchVariantsStats,
} from '@/lib/prefetchStatsPages';

const hoverOnce = new Set<string>();
const idleProductListsOnce = new Set<string>();

type MainNavPrefetchKey =
  | 'products'
  | 'onlineStore'
  | 'settings'
  | 'orders'
  | 'crm'
  | 'callCenter'
  | 'operaciones';

/**
 * Corre tareas de a UNA, y solo cuando el navegador no tiene nada mejor que hacer.
 *
 * 🪤 EL DETALLE QUE IMPORTA ES «DE A UNA». Encolar las cinco en el mismo hueco de reposo las
 *    largaria juntas igual: lo que evita el embotellamiento no es esperar, es no amontonar.
 *
 * 🪤 Y CON `timeout`: sin el, una pestania ocupada podria no darle un hueco nunca. Con el, el
 *    navegador se compromete a correrla — tarde, pero a correrla.
 */
function enReposoDeAUna(tareas: Array<() => void>): void {
  const enReposo = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
    .requestIdleCallback;
  const siguiente = (i: number) => {
    if (i >= tareas.length) return;
    const correr = () => {
      try { tareas[i](); } catch { /* una precarga que falla no rompe nada */ }
      siguiente(i + 1);
    };
    if (typeof enReposo === 'function') enReposo(correr, { timeout: 3000 });
    else setTimeout(correr, 300 * (i + 1));
  };
  siguiente(0);
}
export function prefetchOnMainNavHover(navKey: MainNavPrefetchKey, tenantId: string): void {
  const id = `${navKey}:${tenantId}`;
  if (hoverOnce.has(id)) return;
  hoverOnce.add(id);

  switch (navKey) {
    case 'products':
      prefetchSection('products');
      // ⭐ La LISTA, que es la consulta lenta. Vía el loader de la ruta, para que hover y clic pidan
      // exactamente lo mismo. Si ya está fresca, `ensureQueryData` no hace nada.
      cargarProductos();
      prefetchProductsStats(tenantId).catch(() => {});
      prefetchExistenciasStats(tenantId).catch(() => {});
      prefetchCategoriesStats(tenantId).catch(() => {});
      prefetchModifiersStats(tenantId).catch(() => {});
      prefetchTagsStats(tenantId).catch(() => {});
      prefetchVariantsStats(tenantId).catch(() => {});
      break;
    case 'onlineStore':
      prefetchSection('onlineStore');
      prefetchAbandonedCartsStats(tenantId).catch(() => {});
      prefetchShalomAgenciesStats(tenantId).catch(() => {});
      prefetchEcommercePagesStats(tenantId).catch(() => {});
      break;
    case 'settings':
      prefetchSection('settings');
      prefetchEcommercePaymentMethodsStats(tenantId).catch(() => {});
      prefetchEcommerceDeliveryMethodsStats(tenantId).catch(() => {});
      prefetchEcommerceDeliveryRulesStats(tenantId).catch(() => {});
      prefetchTenantDomainsStats(tenantId).catch(() => {});
      prefetchLogisticsCouriersCoverageStats(tenantId).catch(() => {});
      break;
    case 'orders':
      prefetchSection('orders');
      prefetchEcommerceOrdersStats(tenantId).catch(() => {});
      prefetchSalesOrdersStats(tenantId).catch(() => {});
      prefetchAbandonedCartsStats(tenantId).catch(() => {});
      break;
    case 'crm':
      prefetchSection('crm');
      prefetchSection('callCenter');
      break;
    case 'callCenter':
      prefetchSection('callCenter');
      prefetchSection('crm');
      break;
    case 'operaciones':
      prefetchSection('operaciones');
      /*
       * 🚨 ACA SE PRECARGABA SOLO «VENTAS» Y ESO CREABA UNA ASIMETRIA QUE EL FOUNDER NOTO SIN MEDIR:
       *    «Ventas carga rapido, Por confirmar y Canceladas siguen con el loading — y eso que
       *    Canceladas no tiene data». Tenia razon y no era la consulta: era que al pasar el mouse
       *    por el menu solo UNA de las cinco colas empezaba a cargarse.
       *
       * ⭐ Se precargan las cinco. Comparten pantalla, comparten `loader` y se abren desde el mismo
       *    menu: privilegiar una era una decision vieja —cuando el RUM decia que Ventas era la mas
       *    lenta— que quedo escrita como si fuera permanente.
       *
       * 🪤 Y se recorre la lista en vez de nombrarlas: una cola nueva entra sola. Nombrar cuatro a
       *    mano es como nacio el problema que esto arregla.
       */
      /*
       * 🚨 PRECARGAR LAS CINCO DE GOLPE FUE PEOR QUE PRECARGAR UNA, y el founder lo noto en la
       *    primera prueba: «ahora demoro mas Ventas y Confirmadas cargo rapido». Diez consultas
       *    saliendo juntas compiten entre ellas Y con la pantalla que la persona esta abriendo de
       *    verdad. Se cambio una ventaja injusta por un embotellamiento parejo.
       *
       * ⭐ UNA PRECARGA ES UNA SUGERENCIA, NO UNA ORDEN. Van de a una y solo cuando el navegador
       *    esta libre: si la persona hace clic mientras tanto, el `loader` de esa ruta pide lo
       *    suyo con prioridad normal y la precarga no le saca el lugar. Lo peor que puede pasar
       *    es que no llegue a tiempo — nunca que estorbe.
       */
      enReposoDeAUna(COLAS_DE_OPERACIONES.map((cola) => () => precargarCola(cola)));
      break;
    default:
      break;
  }
}

/**
 * Cuando el hilo está libre (o a los ~4s máx.), precarga listas de productos.
 * Mejora la primera visita a Categorías / Variantes / etc. sin esperar a expandir menú.
 */
export function scheduleIdleProductListPrefetch(tenantId: string): (() => void) | undefined {
  if (idleProductListsOnce.has(tenantId)) return undefined;
  idleProductListsOnce.add(tenantId);

  const run = () => {
    prefetchSection('products');
    prefetchProductsStats(tenantId).catch(() => {});
    prefetchExistenciasStats(tenantId).catch(() => {});
    prefetchCategoriesStats(tenantId).catch(() => {});
    prefetchModifiersStats(tenantId).catch(() => {});
    prefetchTagsStats(tenantId).catch(() => {});
    prefetchVariantsStats(tenantId).catch(() => {});
  };

  if (typeof requestIdleCallback !== 'undefined') {
    const ricId = requestIdleCallback(() => run(), { timeout: 4000 });
    return () => cancelIdleCallback(ricId);
  }
  const t = window.setTimeout(run, 2200);
  return () => clearTimeout(t);
}
